import { ReactNode } from 'react';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { Box, Drawer, List } from '@material-ui/core';
import { TFunction, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import {
  candidateItems,
  companyIsInactiveItems,
  employeeItems,
  recruiterItems,
  adminItems,
  hiringManagerItems,
  reviewerItems,
  recruiterPlusItems,
  designerItems
} from './SidebarConfig';
import SidebarItem from './SidebarItem';
import useAuth from '../../../hooks/useAuth';
import { PATH_HOME } from '../../../routes/paths';
import { RootState } from '../../../redux/store';

const DRAWER_WIDTH = 155;

type TNavItem = {
  icon?: ReactNode;
  iconDisabled?: ReactNode;
  info?: ReactNode;
  href: string;
  title: string;
  openState?: boolean;
  items?: TNavItem[];
  requireSubscraption?: boolean;
};

type ReduceChildParams = {
  array: ReactNode[];
  item: TNavItem;
  pathname: string;
  level: number;
  subscription_id?: string;
  allow_new_job?: boolean;
  t: TFunction<'translation'>;
};

enum DashboardItems {
  INTEGRATIONS = 'Integrations',
  CREATE_NEW_JOB = 'Create New Job Post'
}

function reduceChild({
  array,
  item,
  pathname,
  level,
  subscription_id,
  allow_new_job,
  t
}: ReduceChildParams) {
  const key = item.href + level;
  if (item.items) {
    const match = matchPath({ path: item.href }, pathname);

    return [
      ...array,
      <SidebarItem
        key={key}
        level={level}
        icon={item.icon}
        iconDisabled={item.iconDisabled}
        info={item.info}
        href={item.href}
        title={item.title}
        open={Boolean(match)}
      >
        {renderSidebarItems({
          pathname,
          level: level + 1,
          items: item.items.filter(
            (e) =>
              !(
                e.title === t(DashboardItems.INTEGRATIONS) &&
                subscription_id === 'free'
              ) &&
              !(e.title === t(DashboardItems.CREATE_NEW_JOB) && !allow_new_job)
          ),
          subscription_id,
          t
        })}
      </SidebarItem>
    ];
  }
  return [
    ...array,
    <SidebarItem
      key={key}
      level={level}
      href={item.href}
      iconDisabled={item.iconDisabled}
      icon={item.icon}
      info={item.info}
      title={item.title}
    />
  ];
}

type RenderSidebarItemsParams = {
  items: TNavItem[];
  pathname: string;
  level?: number;
  subscription_id?: string;
  allow_new_job?: boolean;
  t: TFunction<'translation'>;
};

function renderSidebarItems({
  items,
  pathname,
  level = 0,
  subscription_id,
  allow_new_job,
  t
}: RenderSidebarItemsParams) {
  return (
    <List>
      {items.reduce<ReactNode[]>(
        (array, item) =>
          reduceChild({
            array,
            item,
            pathname,
            level,
            subscription_id,
            allow_new_job,
            t
          }),
        []
      )}
    </List>
  );
}

type NavBarProps = {
  isOpenSidebar?: boolean;
  onCloseSidebar?: VoidFunction;
};

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar
}: NavBarProps) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const {
    isAdmin,
    isEmployee,
    isCandidate,
    isRecruiter,
    isRecruiterPlus,
    isHiringManager,
    isReviewer,
    isDesigner
  } = useAuth();

  const company: any = useSelector(
    (state: RootState) => state.generalRecruiter.company
  );
  const adminCompanyInactive = isAdmin && !company.active;

  const getMenuLinks = () => {
    switch (true) {
      case adminCompanyInactive:
        return companyIsInactiveItems(t);

      case isAdmin:
        return adminItems(t, company.is_agency);

      case isCandidate:
        return candidateItems(t);

      case isEmployee:
        return employeeItems(t);

      case isHiringManager:
        return hiringManagerItems(t);

      case isReviewer:
        return reviewerItems(t);

      case isDesigner:
        return designerItems(t);

      case isRecruiterPlus:
        return recruiterPlusItems(t, company.is_agency);

      case isRecruiter:
        return recruiterItems(t, company.is_agency);

      default:
        return recruiterItems(t, company.is_agency);
    }
  };

  const MenuLinks = getMenuLinks();

  // useEffect(() => {
  //   if (isOpenSidebar && onCloseSidebar) {
  //     onCloseSidebar();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [pathname]);

  const getLogoUrl = () =>
    // switch (role) {
    //   case 'recruiter':
    //     return company.active
    //       ? PATH_DASHBOARD.account.showcase
    //       : PATH_DASHBOARD.company.settings;
    //   case 'admin':
    //     return company.active
    //       ? PATH_DASHBOARD.account.showcase
    //       : PATH_DASHBOARD.company.settings;
    //   case 'candidate':
    //     return PATH_DASHBOARD.candidates.getCandidateProfileUrl('me');
    //   case 'employee':
    //     return PATH_DASHBOARD.candidates.getCandidateProfileUrl(candidate_id);
    //   default:
    //     return PATH_DASHBOARD.account.root;
    // }
    PATH_HOME.home;

  const renderContent = (
    <Scrollbar>
      <Box sx={{ px: 1.5, py: 3 }}>
        <RouterLink to={getLogoUrl()}>
          <Logo
            width="131px"
            height="87px"
            url={company?.logo || '/static/img/favicon/icon-192x192.png'}
            style={{ objectFit: 'contain' }}
          />
        </RouterLink>
      </Box>
      {MenuLinks.map((list: any) => (
        <List disablePadding key={list.subheader} sx={{ cursor: 'pointer' }}>
          {renderSidebarItems({
            items: list.items,
            pathname,
            subscription_id: company?.subscription_id,
            allow_new_job: company?.limits?.allow_new_job,
            t
          })}
        </List>
      ))}
    </Scrollbar>
  );

  return (
    <Drawer
      open={isOpenSidebar}
      onClose={onCloseSidebar}
      PaperProps={{
        sx: { width: DRAWER_WIDTH }
      }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH
        }
      }}
      variant="persistent"
      anchor="left"
    >
      {renderContent}
    </Drawer>
  );
}
